// React Compiler opt-out: reads the mutable mandark cache on every render, like useMandarkQueries.
'use no memo';

import {useCallback, useEffect, useMemo} from 'react';
import {List, type Map as ImmutableMap} from 'immutable';
import {resource} from '@albert-io/json-api-framework/request/builder';
import type {StandardModelV1} from '@albert-io/models';
import useMandarkQueries from 'lib/hooks/useMandarkQueries';
import masqueradeStore from 'generic/Masquerade/Masquerade.store';

const MAX_PAGE_SIZE = 250;

export const getQuestionStandardsQuery = (
  questionId: string,
  userId: string | null | undefined,
  page: number
) =>
  resource('standard_v1')
    .mandarkEndpoint(['standards_v1'])
    .filter({questions_v3: {id: questionId}})
    .include('standard_set_v1')
    .withMeta('standard_v1')
    .customQuery({
      meta: {
        context: {
          user: {
            ...(userId ? {id: userId} : {}),
            personalization_settings: true
          }
        }
      }
    })
    .sort('title')
    .pageSize(MAX_PAGE_SIZE)
    .page(page);

/** A question's standards, with the `standard_v1` personalization meta `StandardsList` filters on. */
export const useQuestionStandards = (questionId: string | undefined) => {
  const userId = masqueradeStore.getUserIdByMasqueradeState();

  const firstPageQuery = useMemo(
    () => (questionId ? getQuestionStandardsQuery(questionId, userId, 1) : null),
    [questionId, userId]
  );
  const firstPage = useMandarkQueries(firstPageQuery ? [firstPageQuery] : [], {
    skip: !firstPageQuery
  });

  const totalPages =
    firstPageQuery && firstPage.isReady
      ? (firstPageQuery.getResourceMetadata() as ImmutableMap<string, unknown>).getIn(
          ['page', 'total_pages'],
          1
        )
      : 1;
  const laterPageQueries = useMemo(
    () =>
      questionId
        ? Array.from({length: totalPages - 1}, (_, index) =>
            getQuestionStandardsQuery(questionId, userId, index + 2)
          )
        : [],
    [questionId, userId, totalPages]
  );
  const laterPages = useMandarkQueries(laterPageQueries);

  // A refetching query reads as a single-model placeholder, not a list of standards.
  const pages = [...firstPage.resources, ...laterPages.resources];
  const isReady = firstPage.isReady && laterPages.isReady && pages.every(List.isList);
  const standards = List.of<StandardModelV1>(
    ...(isReady ? (pages as List<StandardModelV1>[]).flatMap((page) => page.toArray()) : [])
  );

  const hasErrored = firstPage.hasErrored || laterPages.hasErrored;
  useEffect(() => {
    if (hasErrored) {
      logger.error('Failed to load the standards of question %s', questionId);
    }
  }, [hasErrored, questionId]);

  // Mandark caches a failed read, so retrying has to re-dirty it.
  const reload = useCallback(() => {
    [firstPageQuery, ...laterPageQueries].forEach((query) => query?.invalidateInterest());
  }, [firstPageQuery, laterPageQueries]);

  return {standards, isReady, hasErrored, reload};
};
